import { Box, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import type { FormulaHighlightSegment } from "../fields/custom-field/utilities/customFormulaHighlight";
import { splitFormulaHighlightSegments } from "../fields/custom-field/utilities/customFormulaHighlight";
import type { FormulaReferenceField } from "../fields/custom-field/utilities/customFormulaParser";

interface Props {
  formula: string;
}

export default function DrillDownFormulaPreview({ formula }: Props) {
  const referenceFields = getReferenceFields(formula);
  const segments = splitFormulaHighlightSegments(formula, referenceFields);
  const groups = splitSegmentsByTopLevelOperators(segments);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
      <Typography variant="body2" color="textSecondary">
        =
      </Typography>
      <Typography component="div" variant="body2" sx={{ whiteSpace: "pre-wrap", flex: 1 }}>
        {groups.map((group, index) => {
          if (group.type === "operator") {
            return (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <Box component="span" key={`${group.value}-${index}`}>
                {group.value}
              </Box>
            );
          }

          return (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Box component="span" key={`group-${index}`} sx={{ whiteSpace: "nowrap" }}>
              {group.segments.map((segment, segmentIndex) => (
                <Box
                  component="span"
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`${segment.text}-${segmentIndex}`}
                  sx={(theme) => ({
                    color:
                      segment.highlightType === "function"
                        ? blue[700]
                        : segment.highlightType === "field"
                          ? theme.palette.warning.dark
                          : "inherit",
                  })}
                >
                  {segment.highlightType === "field" ? stripWrappingQuotes(segment.text) : segment.text}
                </Box>
              ))}
            </Box>
          );
        })}
      </Typography>
    </Box>
  );
}

function splitSegmentsByTopLevelOperators(
  segments: FormulaHighlightSegment[]
): Array<{ type: "group"; segments: FormulaHighlightSegment[] } | { type: "operator"; value: string }> {
  const result: Array<{ type: "group"; segments: FormulaHighlightSegment[] } | { type: "operator"; value: string }> =
    [];
  let currentGroup: FormulaHighlightSegment[] = [];
  let parenthesesDepth = 0;

  segments.forEach((segment) => {
    const parts = segment.text.split(/(\s[+\-*/]\s)/g).filter((item) => item.length > 0);

    parts.forEach((part) => {
      const isOperator = /^\s[+\-*/]\s$/.test(part);
      if (isOperator && parenthesesDepth === 0) {
        if (currentGroup.length > 0) {
          result.push({ type: "group", segments: currentGroup });
          currentGroup = [];
        }
        result.push({ type: "operator", value: part });
        return;
      }

      currentGroup.push({
        ...segment,
        text: part,
      });

      parenthesesDepth = updateParenthesesDepth(parenthesesDepth, part);
    });
  });

  if (currentGroup.length > 0) {
    result.push({ type: "group", segments: currentGroup });
  }

  return result;
}

function updateParenthesesDepth(currentDepth: number, value: string): number {
  let nextDepth = currentDepth;

  for (const character of value) {
    if (character === "(") {
      nextDepth++;
      continue;
    }
    if (character === ")") {
      nextDepth = Math.max(0, nextDepth - 1);
    }
  }

  return nextDepth;
}

function getReferenceFields(formula: string): FormulaReferenceField[] {
  const matches = formula.matchAll(/"((?:[^"\\]|\\.)*)"/g);
  const fields = new Map<string, FormulaReferenceField>();

  for (const match of matches) {
    const value = match[1];
    if (!value || fields.has(value)) {
      continue;
    }

    fields.set(value, { guid: value, caption: value });
  }

  return [...fields.values()];
}

function stripWrappingQuotes(value: string): string {
  if (value.startsWith('"') && value.endsWith('"') && value.length >= 2) {
    return value.slice(1, -1);
  }

  return value;
}
